import { Box, Typography, Link, useTheme, Container } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AuroraGlow } from "../../components/auth/AuroraGlow";
import { makeAuthColors } from "../../components/auth/authStyles";

const PrivacyPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { textPrimary, textSecondary } = makeAuthColors(isDark);
  // Body copy over the glass reads white in dark mode, not the theme's grey.
  const bodyColor = isDark ? "rgba(255, 255, 255, 0.92)" : "#374151";

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
        py: 6,
        px: 2,
      }}
    >
      <AuroraGlow />
      <Container maxWidth="md" sx={{ position: "relative" }}>
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Link component={RouterLink} to="/sign-in" sx={{ display: "inline-block" }}>
            <Box
              component="img"
              src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
              alt="Insight Analytics"
              sx={{ height: 32, width: "auto", display: "block" }}
            />
          </Link>
        </Box>

        {/* Header on the aurora */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "2.25rem", sm: "2.5rem" },
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            color: textPrimary,
            textAlign: "center",
          }}
        >
          Privacy Policy
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            mb: 4,
            fontSize: "0.875rem",
            color: textSecondary,
            textAlign: "center",
          }}
        >
          Last Updated: 28 October 2026
        </Typography>

        {/* Glass content card */}
        <Box
          sx={{
            borderRadius: 4,
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(12px)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            p: { xs: 3, sm: 5 },
            mb: 4,
          }}
        >

          {/* Introduction */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            Welcome to Insight
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: bodyColor, lineHeight: 1.7 }}
          >
            Insight Investing LTD (company number 15628034, registered in England and Wales)
            operates the Insight platform at https://insightanalytics.io (referred to as the
            "Service"). This Privacy Policy explains how we collect, use, and protect information
            when you use our Service. By using the Service, you agree to the collection and use of
            information as described in this policy.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Data Protection Contact:</strong> ben@insightanalytics.io
          </Typography>

          {/* Section 1 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            1. Definitions
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>SERVICE</strong> means the Insight financial research platform operated by
            Insight Investing LTD. <strong>PERSONAL DATA</strong> means data about a living
            individual who can be identified from that data. <strong>USAGE DATA</strong> means data
            collected automatically when you use the Service. <strong>DATA CONTROLLER</strong> means
            the entity that determines how and why personal data is processed (Insight Investing
            LTD). <strong>DATA PROCESSORS</strong> are third parties who process data on our behalf
            (Lemon Squeezy, RevenueCat, OpenRouter, AppsFlyer, data providers).{" "}
            <strong>USER</strong> means you, the
            individual
            using our Service.
          </Typography>

          {/* Section 2 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            2. Information We Collect
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: bodyColor, lineHeight: 1.7 }}
          >
            We collect several types of information to provide and improve our Service.
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            2.1 Personal Data
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            (a) <strong>Account Information:</strong> Email address, name, password (encrypted and
            securely stored). (b) <strong>Payment Information:</strong> Payment details processed
            securely by Lemon Squeezy. We do not directly store your complete credit card details.
            (c) <strong>Portfolio and Watchlist Data:</strong> Stock tickers, companies you track,
            investment portfolios you create, personal notes, preferences, and search queries. (d){" "}
            <strong>Communications:</strong> Support emails and correspondence, feedback you provide.
            We may use your Personal Data to contact you with newsletters, service updates, or
            marketing materials. You may opt out of marketing emails by following the unsubscribe
            link.
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            2.2 Usage Data
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            We collect information automatically when you use our Service, including: pages you
            visit, features you use, time and date of visits, time spent on pages, device type,
            browser type, IP address, general location (city/country level), referring website, and
            actions you take within the Service.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            In our mobile apps we also collect device and advertising identifiers used to measure
            advertising: Apple&apos;s Identifier for Advertisers (IDFA) on iOS, the Google
            Advertising ID on Android, the identifier assigned to your device by AppsFlyer, and
            install referrer data. On iOS the IDFA is only collected if you allow tracking through
            Apple&apos;s App Tracking Transparency prompt. See section 5(f) for who receives this.
          </Typography>

          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: theme.palette.text.primary }}
          >
            2.3 Cookies and Tracking
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We use cookies and similar tracking technologies to keep you logged in, remember your
            preferences, analyze usage patterns, and improve the Service. Types of cookies:{" "}
            <strong>Essential Cookies</strong> (required for the Service to function),{" "}
            <strong>Analytics Cookies</strong> (help us understand usage), and{" "}
            <strong>Preference Cookies</strong> (remember your settings). You can control cookies
            through your browser settings.
          </Typography>

          {/* Section 3 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            3. How We Use Your Information
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We use the information we collect to: (a) provide and maintain our Service, (b) create
            and manage your account, (c) process your subscription payments, (d) provide customer
            support, (e) analyze usage and improve our Service, (f) monitor for technical issues and
            security threats, (g) send you service announcements and account notifications, (h) send
            you our newsletter of product updates and occasional offers, as described in Section 11,
            (i) comply with legal obligations, and (j) for any other purpose with your consent.
          </Typography>

          {/* Section 4 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            4. Third-Party Data Providers
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>IMPORTANT:</strong> All financial data, stock prices, company information, and
            market data displayed in our Service are obtained from third-party data providers,
            primarily Financial Modeling Prep and other external sources. We do not create, verify,
            or guarantee the accuracy of this financial data. We are a data aggregation and
            presentation platform. Any errors, omissions, or inaccuracies in financial data originate
            from these third-party providers, not from us. These third-party data providers do not
            receive your personal information.
          </Typography>

          {/* Section 5 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            5. Who We Share Your Data With
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            We do not sell, rent, or trade your personal information for money. We do share limited
            device and event data with advertising and attribution partners as described in (f)
            below. Otherwise we share your data only with the following trusted service providers:
            (a){" "}
            <strong>Payment Processing:</strong> Lemon Squeezy processes payments and manages
            subscriptions for purchases made on the web. Purchases made in our mobile apps are
            processed by Apple (App Store) or Google (Google Play), with subscription state managed
            through RevenueCat. (b) <strong>AI Processing:</strong> when you use Insight AI features
            (such as chat and AI-generated reports), your messages and queries are sent to
            OpenRouter, which routes them to one of several third-party AI model providers to
            generate the response. This content is shared solely to provide the feature. (c){" "}
            <strong>Hosting and Infrastructure:</strong> cloud hosting providers who store our
            databases and host our application. (d) <strong>Analytics:</strong> analytics services
            to help us understand Service usage. (e) <strong>Email and Push:</strong> an email
            provider used to send account emails such as verification and password resets, and the
            alerts and newsletter described in Section 11; push notifications on our mobile
            app are delivered through Apple&apos;s push notification service. (f){" "}
            <strong>Advertising and Attribution:</strong> in our mobile apps we use AppsFlyer, a
            mobile measurement partner, to understand which advertising campaigns lead people to
            install Insight and subscribe. AppsFlyer receives device identifiers, your account
            identifier, and app and purchase events, and shares attributed results with the
            advertising networks we run campaigns on, such as TikTok. On iOS this only happens if
            you allow it through Apple&apos;s App Tracking Transparency prompt. All service
            providers are contractually required to protect your data.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We may disclose your information if required to do so by law or in response to court
            orders, legal processes, requests from law enforcement or government authorities,
            protection of our rights, property, or safety, or investigation of fraud or security
            issues.
          </Typography>

          {/* Section 6 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            6. Data Retention
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            We retain your Personal Data for as long as necessary to provide the Service. Retention
            periods: <strong>Active Accounts</strong> (data retained while your account is active),{" "}
            <strong>Closed Accounts</strong> (data deleted within 30 days of account closure unless
            required for legal reasons, except the bounce and complaint list described in Section 11,
            which we keep after you delete your account), <strong>Transaction Records</strong> (retained
            for 7 years for accounting and tax purposes), <strong>Analytics Data</strong> (retained for
            up to 2 years), and <strong>Legal Holds</strong> (data may be retained longer if required
            by law or legal proceedings).
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            You can request deletion of your account at any time by contacting us at
            support@insightanalytics.io. Once deleted, your personal data will be removed from our
            systems within 30 days, apart from the bounce and complaint list described in Section 11.
            Deletion is permanent and cannot be reversed.
          </Typography>

          {/* Section 7 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            7. Data Security
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            The security of your data is important to us. We implement industry-standard security
            measures to protect your Personal Data, including encryption of data in transit
            (SSL/TLS), encryption of sensitive data at rest, secure authentication and access
            controls, regular security assessments, and employee training on data protection.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            However, no method of transmission over the internet is 100% secure. While we strive to
            protect your Personal Data, we cannot guarantee absolute security.
          </Typography>

          {/* Section 8 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            8. Your Data Protection Rights (UK and EU)
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            If you are a resident of the United Kingdom or European Economic Area, you have certain
            data protection rights under the GDPR: (a) <strong>Right to Access:</strong> You can
            request a copy of the personal data we hold about you. (b){" "}
            <strong>Right to Rectification:</strong> You can request correction of inaccurate or
            incomplete data. (c) <strong>Right to Erasure:</strong> You can request deletion of your
            personal data. (d) <strong>Right to Restriction:</strong> You can request that we
            restrict processing of your data. (e) <strong>Right to Data Portability:</strong> You can
            receive your data in a structured, machine-readable format. (f){" "}
            <strong>Right to Object:</strong> You can object to our processing of your personal data.
            (g) <strong>Right to Withdraw Consent:</strong> You can withdraw consent at any time
            where we rely on consent. (h) <strong>Right to Complain:</strong> You can lodge a
            complaint with the Information Commissioner's Office (ICO).
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            To exercise any of these rights, please email us at ben@insightanalytics.io. We will
            respond to your request within one month.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>ICO Contact:</strong> https://ico.org.uk/, Telephone: 0303 123 1113.
          </Typography>

          {/* Section 9 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            9. International Data Transfers
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            Your personal data may be transferred to and processed in countries outside the UK and
            European Economic Area. When we transfer data internationally, we ensure appropriate
            safeguards are in place: Standard Contractual Clauses approved by the European
            Commission, transfers to countries with adequacy decisions, and your explicit consent
            where required.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            Our primary data processing occurs within the European Economic Area and United Kingdom.
          </Typography>

          {/* Section 10 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            10. Children's Privacy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            The Service is not intended for individuals under the age of 18. We do not knowingly
            collect personal information from children under 18. If you are a parent or guardian and
            believe your child has provided us with personal information, please contact us
            immediately.
          </Typography>

          {/* Section 11 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            11. Notifications and Marketing Communications
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Alerts you ask for.</strong> When you turn on alerts for a company or topic, we
            use your email address, your alert choices and, on our mobile app, your device's push
            token to deliver those alerts in the app, by email and as push notifications. We do
            this to provide a service you have requested.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Our newsletter.</strong> We send our newsletter by email to every account,
            unless you opt out. It brings product updates about new features and improvements to our
            own similar services and occasional offers on those services, such as discounts or
            upgrades. When you create an account we tell
            you this, and that you can opt out at any time (this is known as the "soft opt-in"). If
            your account was created before this version of our Privacy Policy, we added it to the
            newsletter when this version took effect, unless you had already switched these emails
            off or unsubscribed from them. We keep a record of each choice, including when and how
            it was made, while you have an account, and delete it with your account.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Your controls.</strong> Every newsletter and alert email contains a one-click
            unsubscribe link. Unsubscribing from any newsletter email, or switching off email for
            "Updates and offers" under Notifications in your account settings, stops all newsletter
            emails, product updates and offers alike. Alerts you have set up are separate: they have
            their own unsubscribe link and carry on if you leave the newsletter. You can change your
            email and push preferences at any time under Notifications in your account settings, or
            by emailing us at support@insightanalytics.io. Push notifications are only sent if you
            have allowed them on your device, and you can turn them off at any time in your device
            settings.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            <strong>Bounces and complaints.</strong> Our email provider tells us when an email
            permanently fails to deliver or is marked as spam. When that happens we add the address
            to a suppression list and stop sending non-essential email to it. The list holds only the
            address, whether it bounced or was marked as spam, and when. We keep it for as long as
            needed, even after you delete your account, so that we don't email an address that
            bounced or complained. Your other email choices, including any unsubscribe, are deleted
            with your account. If you later sign up again with the same address, that is a new
            account: we tell you about the newsletter when you sign up, as we do for everyone, and an
            address on the suppression list still gets no non-essential email.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            Even if you opt out of alerts and the newsletter, you will still receive important
            service-related emails (email verification, password resets, billing information,
            security alerts, etc.).
          </Typography>

          {/* Section 12 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            12. California Privacy Rights (CCPA)
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            If you are a California resident, you have additional rights under the California
            Consumer Privacy Act (CCPA): (a) <strong>Right to Know:</strong> You can request
            information about the personal data we collect, use, and disclose. (b){" "}
            <strong>Right to Delete:</strong> You can request deletion of your personal data. (c){" "}
            <strong>Right to Opt-Out of Sale or Sharing:</strong> We do not sell your personal
            information for money. We do share device identifiers and app events with advertising
            and attribution partners for advertising measurement, which the CCPA/CPRA treats as
            &quot;sharing&quot; for cross-context behavioural advertising. In our iOS app you can
            stop this by declining Apple&apos;s App Tracking Transparency prompt, or at any time
            under Settings &rsaquo; Privacy &amp; Security &rsaquo; Tracking. You can also opt out
            by emailing us using the address below.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            To exercise your California data protection rights, please email us at
            ben@insightanalytics.io.
          </Typography>

          {/* Section 13 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            13. Changes to This Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: bodyColor, lineHeight: 1.7 }}
          >
            We may update this Privacy Policy from time to time. When we make changes, we will update
            the "Last Updated" date at the top of this policy, notify you by email (for material
            changes), and post a notice on our website. Your continued use of the Service after any
            changes constitutes your acceptance of the updated Privacy Policy.
          </Typography>

          {/* Contact Information */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}
          >
            14. Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: bodyColor, lineHeight: 1.7 }}
          >
            If you have questions, concerns, or requests about this Privacy Policy or our data
            practices, please contact us:
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 1, color: bodyColor, lineHeight: 1.7 }}
          >
            Email: support@insightanalytics.io
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: bodyColor, lineHeight: 1.7 }}
          >
            Data Protection Inquiries: ben@insightanalytics.io
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 1, color: bodyColor, fontWeight: 600 }}
          >
            Company Details:
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Insight Investing LTD
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Company Number: 15628034
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 0.5, color: bodyColor }}
          >
            Registered in England and Wales
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 4, color: bodyColor }}
          >
            Full registered office address: Available on our website footer
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: bodyColor, fontStyle: "italic" }}
          >
            We will respond to all inquiries and requests in accordance with applicable data
            protection laws.
          </Typography>
        </Box>

        {/* Back Link */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Link
            component={RouterLink}
            to="/sign-in"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "0.9375rem",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPage;
